import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { formatWeekRange, formatWeekOptionLabel, weekMonthName, weekNumberForDate, shortDateOrdinal, formatFullDate } from '../utils/forSale';

// Reference year for week-range date labels when no specific date is picked
// (matches the "2026 Dates" convention used throughout the property page).
const WEEK_LABEL_YEAR = 2026;
const ALL_WEEKS = Array.from({ length: 52 }, (_, i) => i + 1);
const MOBILE_BREAKPOINT = 768;

// Below 768px the panel becomes a fullscreen overlay portaled to <body> — it
// must escape the property page's `.property-listings-scroll` ancestor
// (a -webkit-overflow-scrolling: touch container), since iOS Safari clips
// and drags along any position:fixed element nested inside one instead of
// pinning it to the viewport.
function useIsMobilePanel() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < MOBILE_BREAKPOINT); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

export const DEFAULT_FORSALE_FILTERS = {
  viewType: '',   // '' | 'ocean' | 'courtyard'
  bedroom: '',    // '' | 'Studio' | '1 Bedroom' | '2 Bedroom'
  priceMin: '',
  priceMax: '',
  maintMin: '',
  maintMax: '',
  weekFrom: '',
  weekTo: '',
  specificDate: '',
  sortBy: 'week', // 'week' | 'price_asc' | 'price_desc' | 'maint_asc'
};

function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
    </svg>
  );
}

function XIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// Each active filter criterion becomes one removable chip. Price/maintenance-fee/
// date each collapse min+max (or from+to) into a single chip since they're one
// criterion conceptually, even though they're two draft fields.
function buildChips(filters) {
  const chips = [];
  if (filters.viewType === 'ocean')     chips.push({ key: 'viewType', label: 'Ocean View' });
  if (filters.viewType === 'courtyard') chips.push({ key: 'viewType', label: 'Courtyard View' });
  if (filters.bedroom) chips.push({ key: 'bedroom', label: filters.bedroom });
  if (filters.priceMin || filters.priceMax) {
    let label;
    if (filters.priceMin && filters.priceMax) label = `$${filters.priceMin}–$${filters.priceMax}`;
    else if (filters.priceMax) label = `Under $${filters.priceMax}`;
    else label = `Over $${filters.priceMin}`;
    chips.push({ key: 'price', label });
  }
  if (filters.maintMin || filters.maintMax) {
    let label;
    if (filters.maintMin && filters.maintMax) label = `Maint. Fee $${filters.maintMin}–$${filters.maintMax}`;
    else if (filters.maintMax) label = `Maint. Fee Under $${filters.maintMax}`;
    else label = `Maint. Fee Over $${filters.maintMin}`;
    chips.push({ key: 'maint', label });
  }
  if (filters.specificDate) {
    chips.push({ key: 'weeks', label: `Week ${filters.weekFrom} - ${shortDateOrdinal(filters.specificDate)}` });
  } else if (filters.weekFrom || filters.weekTo) {
    const monA = weekMonthName(Number(filters.weekFrom), WEEK_LABEL_YEAR);
    const monB = weekMonthName(Number(filters.weekTo), WEEK_LABEL_YEAR);
    let label;
    if (filters.weekFrom && filters.weekTo) {
      const months = monA === monB ? monA : `${monA} - ${monB}`;
      label = `Weeks ${filters.weekFrom}-${filters.weekTo} (${months})`;
    } else if (filters.weekTo) {
      label = `Until Week ${filters.weekTo} (${monB})`;
    } else {
      label = `From Week ${filters.weekFrom} (${monA})`;
    }
    chips.push({ key: 'weeks', label });
  }
  return chips;
}

const CLEARED_FIELDS_BY_CHIP = {
  viewType: { viewType: '' },
  bedroom:  { bedroom: '' },
  price:    { priceMin: '', priceMax: '' },
  maint:    { maintMin: '', maintMax: '' },
  weeks:    { weekFrom: '', weekTo: '', specificDate: '' },
};

function DualRangeSlider({ min, max, lo, hi, onChangeLo, onChangeHi }) {
  if (min >= max) return null;
  const pctLo = ((lo - min) / (max - min)) * 100;
  const pctHi = ((hi - min) / (max - min)) * 100;
  const step = Math.max(1, Math.round((max - min) / 100));
  return (
    <div className="dual-range-slider">
      <div className="dual-range-track" />
      <div className="dual-range-fill" style={{ left: `${pctLo}%`, right: `${100 - pctHi}%` }} />
      <input
        type="range" min={min} max={max} step={step} value={lo}
        className="dual-range-input"
        onChange={e => onChangeLo(Math.min(Number(e.target.value), hi))}
        aria-label="Minimum asking price"
      />
      <input
        type="range" min={min} max={max} step={step} value={hi}
        className="dual-range-input"
        onChange={e => onChangeHi(Math.max(Number(e.target.value), lo))}
        aria-label="Maximum asking price"
      />
    </div>
  );
}

const toggleBtnStyle = active => ({
  padding: 'var(--space-2) var(--space-4)', minHeight: 38, borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
  border: active ? '1px solid var(--brand-dark)' : '1px solid var(--border-strong)',
  background: active ? 'var(--brand-dark)' : 'var(--surface)',
  color: active ? 'var(--surface)' : 'var(--ink)',
  flex: '1 1 0',
  whiteSpace: 'nowrap',
});

const numInput = {
  padding: '0.5rem 0.65rem', minHeight: 38, border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-sm)', width: '100%', boxSizing: 'border-box', background: 'var(--surface)',
};

// Dropdown listing weeks with their resolved date range as the option text,
// plus stacked spinner buttons that step through the same options list —
// keyboard/mouse users can either open the dropdown or click through weeks.
function WeekSelect({ value, onChange, options, placeholder, ariaLabel }) {
  const idx = options.indexOf(Number(value));
  function step(delta) {
    if (!options.length) return;
    const nextIdx = idx === -1
      ? (delta > 0 ? 0 : options.length - 1)
      : Math.min(options.length - 1, Math.max(0, idx + delta));
    onChange(String(options[nextIdx]));
  }
  return (
    <div className="week-select-row">
      <select value={value} onChange={e => onChange(e.target.value)} style={numInput} aria-label={ariaLabel}>
        <option value="">{placeholder}</option>
        {options.map(w => (
          <option key={w} value={w}>{formatWeekOptionLabel(w, WEEK_LABEL_YEAR)}</option>
        ))}
      </select>
      <div className="week-select-spinner">
        <button type="button" onClick={() => step(1)} disabled={idx !== -1 && idx === options.length - 1} aria-label={`Next ${ariaLabel}`}>▲</button>
        <button type="button" onClick={() => step(-1)} disabled={idx === 0} aria-label={`Previous ${ariaLabel}`}>▼</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.35rem' }}>
      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.55rem' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ── Realtor.com-style filter trigger + panel + active-filter chip bar ───────
export default function ForSaleFilterPanel({ filters, onChange, listings, resultCount }) {
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [draft, setDraft] = useState(filters);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const isMobile = useIsMobilePanel();

  useEffect(() => {
    if (!open) { setEntered(false); return; }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Lock background scroll while the fullscreen mobile overlay is open —
  // otherwise the page behind scrolls along with (or instead of) the panel.
  useEffect(() => {
    if (!open || !isMobile) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open, isMobile]);

  function handleDragStart(e) {
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transition = 'none';
    function onMove(ev) {
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      panel.style.transform = `translateY(${Math.max(0, y - startY)}px)`;
    }
    function onEnd(ev) {
      const y = ev.changedTouches ? ev.changedTouches[0].clientY : ev.clientY;
      const delta = Math.max(0, y - startY);
      panel.style.transition = '';
      panel.style.transform = '';
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      if (delta > 100) setOpen(false);
    }
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e) {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function openPanel() {
    setDraft(filters);
    setOpen(true);
  }

  function clearAll() {
    setDraft(DEFAULT_FORSALE_FILTERS);
    onChange(DEFAULT_FORSALE_FILTERS);
  }

  function applyFilters() {
    onChange(draft);
    setOpen(false);
  }

  function removeChip(key) {
    onChange({ ...filters, ...CLEARED_FIELDS_BY_CHIP[key] });
  }

  function setDraftField(field, value) {
    setDraft(d => ({ ...d, [field]: value }));
  }

  // Week range and specific-date are mutually exclusive ways to express the
  // same underlying filter: editing a week field directly clears the date
  // (since the range no longer necessarily matches it), picking a date
  // overwrites both week fields to match it, and clearing the date clears
  // the range it had populated.
  function setWeekFrom(value) {
    setDraft(d => {
      const fromNum = Number(value) || 0;
      const toNum = Number(d.weekTo) || 0;
      // To Week must stay strictly greater than From Week — bump it forward
      // if the current value no longer qualifies.
      const weekTo = (value && toNum <= fromNum) ? String(Math.min(52, fromNum + 1)) : d.weekTo;
      return { ...d, weekFrom: value, weekTo, specificDate: '' };
    });
  }
  function setWeekTo(value) {
    setDraft(d => ({ ...d, weekTo: value, specificDate: '' }));
  }
  function setSpecificDate(value) {
    if (!value) {
      setDraft(d => ({ ...d, specificDate: '', weekFrom: '', weekTo: '' }));
      return;
    }
    const { week } = weekNumberForDate(value);
    const clamped = String(Math.min(52, Math.max(1, week)));
    // Both dropdowns land on the same week; the user can widen To Week afterward.
    setDraft(d => ({ ...d, specificDate: value, weekFrom: clamped, weekTo: clamped }));
  }

  // To Week's options exclude weeks <= From Week, except we keep the currently
  // selected value visible even if it no longer qualifies (e.g. right after a
  // specific-date pick sets both fields to the same week) so the <select>
  // never renders with no matching option.
  const toWeekOptions = useMemo(() => {
    const fromNum = Number(draft.weekFrom) || 0;
    const opts = ALL_WEEKS.filter(w => w > fromNum);
    const currentTo = Number(draft.weekTo);
    if (currentTo && !opts.includes(currentTo)) {
      opts.push(currentTo);
      opts.sort((a, b) => a - b);
    }
    return opts;
  }, [draft.weekFrom, draft.weekTo]);

  const priceBounds = useMemo(() => {
    if (!listings.length) return { min: 0, max: 100 };
    const cents = listings.map(l => l.asking_price_cents);
    return { min: Math.floor(Math.min(...cents) / 100), max: Math.ceil(Math.max(...cents) / 100) };
  }, [listings]);

  const chips = useMemo(() => buildChips(filters), [filters]);
  const activeCount = chips.length;

  const sliderLo = draft.priceMin !== '' ? Number(draft.priceMin) : priceBounds.min;
  const sliderHi = draft.priceMax !== '' ? Number(draft.priceMax) : priceBounds.max;

  return (
    <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          ref={triggerRef}
          type="button"
          className="filter-panel-trigger"
          onClick={() => (open ? setOpen(false) : openPanel())}
          aria-expanded={open}
        >
          <FilterIcon />
          Filters{activeCount > 0 ? ` (${activeCount} active)` : ''}
        </button>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
          {resultCount} listing{resultCount !== 1 ? 's' : ''}
        </span>
      </div>

      {open && (() => {
        const panel = (
          <>
            <div className="filter-panel-overlay" onClick={() => setOpen(false)} />
          <div className={`filter-panel-panel${entered ? ' open' : ''}`} ref={panelRef}>
            <div className="filter-panel-drag-handle" onTouchStart={handleDragStart} onMouseDown={handleDragStart}>
              <span />
            </div>
            <div className="filter-panel-panel-header">
              <span>Filters</span>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close filters" className="filter-panel-close">
                <XIcon size={18} />
              </button>
            </div>

            <div className="filter-panel-panel-body">
              <Section title="View Type">
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" style={toggleBtnStyle(draft.viewType === '')} onClick={() => setDraftField('viewType', '')}>All</button>
                  <button type="button" style={toggleBtnStyle(draft.viewType === 'ocean')} onClick={() => setDraftField('viewType', 'ocean')}>Ocean View</button>
                  <button type="button" style={toggleBtnStyle(draft.viewType === 'courtyard')} onClick={() => setDraftField('viewType', 'courtyard')}>Courtyard View</button>
                </div>
              </Section>

              <Section title="Bedrooms">
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" style={toggleBtnStyle(draft.bedroom === '')} onClick={() => setDraftField('bedroom', '')}>All</button>
                  <button type="button" style={toggleBtnStyle(draft.bedroom === 'Studio')} onClick={() => setDraftField('bedroom', 'Studio')}>Studio</button>
                  <button type="button" style={toggleBtnStyle(draft.bedroom === '1 Bedroom')} onClick={() => setDraftField('bedroom', '1 Bedroom')}>1 Bedroom</button>
                  <button type="button" style={toggleBtnStyle(draft.bedroom === '2 Bedroom')} onClick={() => setDraftField('bedroom', '2 Bedroom')}>2 Bedroom</button>
                </div>
              </Section>

              <Section title="Asking Price">
                <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem' }}>
                  <input type="number" min="0" placeholder={`$${priceBounds.min}`} value={draft.priceMin} onChange={e => setDraftField('priceMin', e.target.value)} style={numInput} />
                  <span style={{ color: 'var(--ink-faint)', alignSelf: 'center' }}>–</span>
                  <input type="number" min="0" placeholder={`$${priceBounds.max}`} value={draft.priceMax} onChange={e => setDraftField('priceMax', e.target.value)} style={numInput} />
                </div>
                <DualRangeSlider
                  min={priceBounds.min} max={priceBounds.max}
                  lo={sliderLo} hi={sliderHi}
                  onChangeLo={v => setDraftField('priceMin', String(v))}
                  onChangeHi={v => setDraftField('priceMax', String(v))}
                />
              </Section>

              <Section title="Annual Maintenance Fee">
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <input type="number" min="0" placeholder="Min" value={draft.maintMin} onChange={e => setDraftField('maintMin', e.target.value)} style={numInput} />
                  <span style={{ color: 'var(--ink-faint)', alignSelf: 'center' }}>–</span>
                  <input type="number" min="0" placeholder="Max" value={draft.maintMax} onChange={e => setDraftField('maintMax', e.target.value)} style={numInput} />
                </div>
              </Section>

              <Section title="Dates">
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', marginBottom: '0.25rem' }}>From Week</div>
                    <WeekSelect value={draft.weekFrom} onChange={setWeekFrom} options={ALL_WEEKS} placeholder="Any" ariaLabel="From Week" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', marginBottom: '0.25rem' }}>To Week</div>
                    <WeekSelect value={draft.weekTo} onChange={setWeekTo} options={toWeekOptions} placeholder="Any" ariaLabel="To Week" />
                  </div>
                </div>

                <div style={{ borderTop: '1px dashed var(--border)', margin: '0.9rem 0 0.75rem' }} />

                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', marginBottom: '0.25rem' }}>Looking for a specific date?</div>
                <input type="date" value={draft.specificDate} onChange={e => setSpecificDate(e.target.value)} style={numInput} />
                {draft.specificDate && (() => {
                  const { week, year } = weekNumberForDate(draft.specificDate);
                  return (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--brand-dark)', marginTop: '0.4rem', fontWeight: 600 }}>
                      {formatFullDate(draft.specificDate)} falls in Week {week} ({formatWeekRange(week, year)})
                    </div>
                  );
                })()}
              </Section>

              <Section title="Sort By">
                <select value={draft.sortBy} onChange={e => setDraftField('sortBy', e.target.value)} style={numInput}>
                  <option value="week">Week Number</option>
                  <option value="price_asc">Price — Low to High</option>
                  <option value="price_desc">Price — High to Low</option>
                  <option value="maint_asc">Maintenance Fee</option>
                </select>
              </Section>
            </div>

            <div className="filter-panel-panel-footer">
              <button type="button" className="filter-panel-clear-btn" onClick={clearAll}>Clear All</button>
              <button type="button" className="filter-panel-apply-btn" onClick={applyFilters}>Apply Filters</button>
            </div>
          </div>
          </>
        );
        return isMobile ? createPortal(panel, document.body) : panel;
      })()}

      {chips.length > 0 && (
        <div className="filter-panel-chips">
          {chips.map(chip => (
            <span key={chip.key} className="filter-panel-chip">
              {chip.label}
              <button type="button" onClick={() => removeChip(chip.key)} aria-label={`Remove ${chip.label} filter`}>
                <XIcon size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
